import React, { useCallback, useEffect, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import {
  Banner,
  Button,
  Checkbox,
  Footer,
  Input,
  KeyboardScreenContainer,
  Text,
} from '~/components';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { isWeb } from '~/utils/device';
import { isCpf, isFullName } from '~/utils/yup';
import type { StepComponentProps } from '~/components/StepsForm';
import { MarisaAppUtils } from '~/utils/MarisaAppUtils';
import { GENERAL_CONDITIONS_LINK } from '~/scenes/IncomeProtection/constants';
import type { BagProtectionForm } from '~/@types/BagProtection/form';
import { useIncomeProtectionForm, useSalesFunnel } from '~/stores';
import { INCOME_PROTECTION_ACCESSION_TERMS_LINK } from '~/utils/url';
import { LocalStorage } from '~/storage/LocalStorage';
import type { AnonymousUserState } from '~/storage/types';
import { ANONYMOUS_USER } from '~/storage/constants';

const schema = yup
  .object()
  .shape({
    fullName: isFullName(),
    document: isCpf(),
    acceptGeneralConditions: yup.bool().isTrue().required(),
    acceptAccessionTerms: yup.bool().isTrue().required(),
  })
  .required();

const IncomeProtectionContractIntroduction: React.FC<StepComponentProps> = ({
  onContinue,
}) => {
  const { setIntroduction } = useIncomeProtectionForm();
  const { setInfos } = useSalesFunnel();

  const [isCpfInputDisabled, setIsCpfInputDisabled] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<BagProtectionForm.Introduction>({
    resolver: yupResolver(schema),
  });

  const onSubmit = useCallback(
    (data: IncomeProtectionForm.Introduction) => {
      setIntroduction(data);
      setInfos({
        fullName: data.fullName,
        documentNumber: data.document,
      });
      onContinue?.();
    },
    [onContinue, setInfos, setIntroduction]
  );

  useEffect(() => {
    async function verifyAnonymousUser() {
      const anonymousUserStorage = await LocalStorage.get<AnonymousUserState>(
        ANONYMOUS_USER
      );

      if (anonymousUserStorage && !anonymousUserStorage?.isNotInsured) {
        setValue('document', anonymousUserStorage.document);
      }

      setIsCpfInputDisabled(
        !!anonymousUserStorage && !anonymousUserStorage.isNotInsured && isWeb
      );
    }

    verifyAnonymousUser();
  }, [setValue]);

  return (
    <KeyboardScreenContainer
      footer={
        <View className="flex xl:hidden">
          <Footer onSubmit={handleSubmit(onSubmit)} />
        </View>
      }
    >
      <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
        <Banner type="income-protection" />

        <View className="mt-8 xl:mt-0 px-6 flex-1 xl:ml-24">
          <Text className="text-xl text-gray-600">
            Olá, bem vinda ao{' '}
            <Text className="text-primary-600">Proteção Perda de Renda</Text>
          </Text>

          <Text className="mt-4 text-gray-400 text-sm mb-8">
            Para começarmos, insira abaixo as informações solicitadas:
          </Text>

          <Input
            control={control}
            name="fullName"
            label="Nome completo do segurado"
            placeholder="Insira o nome completo do segurado"
            error={errors.fullName?.message}
            keyboardType="ascii-capable"
            maxLength={100}
          />

          <Input
            control={control}
            name="document"
            label="CPF do segurado"
            placeholder="Insira o CPF do segurado"
            error={errors.document?.message}
            maskConfig={{ type: 'cpf' }}
            keyboardType="number-pad"
            disabled={isCpfInputDisabled}
          />

          <View className="flex-1 justify-end">
            <Checkbox
              control={control}
              name="acceptGeneralConditions"
              className="mt-6"
              error={errors.acceptGeneralConditions?.message}
            >
              <View className="flex-row items-center">
                <Text className="text-xs text-gray-600">
                  Eu concordo com as{' '}
                </Text>
                <TouchableOpacity
                  activeOpacity={0.6}
                  onPress={() =>
                    MarisaAppUtils.openUrl(GENERAL_CONDITIONS_LINK)
                  }
                >
                  <Text className="text-primary-600 font-medium text-xs">
                    Condições Gerais
                  </Text>
                </TouchableOpacity>
              </View>
            </Checkbox>

            <Checkbox
              control={control}
              name="acceptAccessionTerms"
              className="my-4"
              error={errors.acceptAccessionTerms?.message}
            >
              <View className="flex-row items-center">
                <Text className="text-xs text-gray-600">
                  Eu concordo com os{' '}
                </Text>
                <TouchableOpacity
                  activeOpacity={0.6}
                  onPress={() =>
                    MarisaAppUtils.openUrl(
                      INCOME_PROTECTION_ACCESSION_TERMS_LINK
                    )
                  }
                >
                  <Text className="text-primary-600 font-medium text-xs">
                    Termos de Adesão
                  </Text>
                </TouchableOpacity>
              </View>
            </Checkbox>
            {isWeb && (
              <View className="flex-1 justify-end hidden xl:flex">
                <Button className="w-full" onPress={handleSubmit(onSubmit)}>
                  CONTINUAR
                </Button>
              </View>
            )}
          </View>
        </View>
      </View>
    </KeyboardScreenContainer>
  );
};

export default IncomeProtectionContractIntroduction;
